import SoftBadge from "@/components/atoms/SoftBadge";
import AreYouSurToContinueModal from "@/components/molecules/modals/AreYouSurToContinueModal";
import AllOrganisationsHeader from "@/components/molecules/table-headers/AllOrganisationsHeader";
import Table from "@/components/molecules/table/Table";
import { useDebouncedValue } from "@/hooks/useDebouncedValue";
import { useTableState } from "@/hooks/useTableState";
import IFile from "@/interfaces/File";
import Organisation from "@/interfaces/Organisation";
import { useDownloadFileMutation } from "@/services/files";
import {
  useDeleteOrganisationMutation,
  useGetOrganisationsQuery,
} from "@/services/organisations";
import {
  faDownload,
  faEdit,
  faExternalLinkAlt,
  faEye,
  faTrash,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { FetchBaseQueryError } from "@reduxjs/toolkit/dist/query";
import { ColumnDef, TableState } from "@tanstack/react-table";
import { DateTime } from "luxon";
import { ElementRef, FC, Fragment, useRef } from "react";
import { Button, OverlayTrigger, Tooltip } from "react-bootstrap";
import { Link } from "react-router-dom";
import { ToastContentProps, toast } from "react-toastify";

const OrganisationsView: FC = () => {
  const DeleteModalRef =
    useRef<ElementRef<typeof AreYouSurToContinueModal>>(null);
  const [deleteOrganisationAsync] = useDeleteOrganisationMutation();
  const [downloadFileAsync] = useDownloadFileMutation();

  const [state, setState] = useTableState<Partial<TableState>>(
    "organizations",
    {
      pagination: {
        pageIndex: 0,
        pageSize: 15,
      },
      globalFilter: "",
    },
  );

  const debouncedQuery = useDebouncedValue(state.globalFilter, 500);

  const { data } = useGetOrganisationsQuery({
    ...state.pagination,
    query: debouncedQuery,
    ...(state.sorting?.[0] && {
      sortBy: state.sorting[0].id,
      sortDirection: state.sorting[0].desc ? "desc" : "asc",
    }),
  });

  const onDelete = async (id: string) => {
    toast.promise(deleteOrganisationAsync(id).unwrap(), {
      pending: "Suppression de la catégorie en cours ...",
      success: {
        render() {
          DeleteModalRef.current?.showOrHiddenModal();
          return (
            <p className="mb-0 text-center">L'opco à bien été supprimé 🤩</p>
          );
        },
      },
      error: {
        render({ data }: ToastContentProps<FetchBaseQueryError>) {
          return "Une erreur est survenue";
        },
      },
    });
  };

  const onDownload = (file: IFile) => {
    toast.promise(downloadFileAsync(file).unwrap(), {
      pending: "Téléchargement de la convention...",
      success: "Convention téléchargée avec succès",
      error: "Erreur lors du téléchargement de la convention",
    });
  };

  const columns = useRef<ColumnDef<Organisation>[]>([
    {
      header: "Actions",
      enableSorting: false,
      footer: (props) => props.column.id,
      accessorFn: ({ id, convention }) => (
        <Fragment>
          {convention && (
            <Fragment>
              <OverlayTrigger
                overlay={<Tooltip id="nextTooltip">Télécharger</Tooltip>}
              >
                <Button
                  variant="success"
                  size="sm"
                  className="border-300 me-1 text-600"
                  onClick={() => onDownload(convention)}
                >
                  <FontAwesomeIcon icon={faDownload} color="white" />
                </Button>
              </OverlayTrigger>
              <OverlayTrigger
                overlay={<Tooltip id="nextTooltip">Voir</Tooltip>}
              >
                <Link
                  className="btn btn-sm btn-info border-300 me-1 text-600"
                  to={convention.link}
                  target="_blank"
                >
                  <FontAwesomeIcon icon={faEye} color="white" />
                </Link>
              </OverlayTrigger>
            </Fragment>
          )}
          <OverlayTrigger
            overlay={<Tooltip id="nextTooltip">Modifier</Tooltip>}
          >
            <Link
              to={`/organizations/${id}/edit`}
              className="btn btn-info btn-sm me-1"
            >
              <FontAwesomeIcon icon={faEdit} />
            </Link>
          </OverlayTrigger>
          <OverlayTrigger
            overlay={<Tooltip id="nextTooltip">Supprimer</Tooltip>}
          >
            <Button
              variant="danger"
              size="sm"
              className="border-300 text-600"
              onClick={() => DeleteModalRef.current?.showOrHiddenModal(id)}
            >
              <FontAwesomeIcon icon={faTrash} color="white" />
            </Button>
          </OverlayTrigger>
        </Fragment>
      ),
      id: "actions",
      cell: (info) => info.getValue(),
    },
    {
      header: "#",
      footer: (props) => props.column.id,
      accessorFn: ({ id }) => id,
      id: "id",
      cell: (info) => info.getValue(),
    },
    {
      header: "Nom",
      footer: (props) => props.column.id,
      accessorFn: ({ name, backgroundColor, textColor }) => (
        <SoftBadge bg={backgroundColor} textColor={textColor}>
          {name}
        </SoftBadge>
      ),
      id: "name",
      cell: (info) => info.getValue(),
    },
    {
      header: "Catalogue",
      footer: (props) => props.column.id,
      enableSorting: false,
      accessorFn: ({ catalogUrl }) =>
        catalogUrl ? (
          <a href={catalogUrl} target="_blank" rel="noopener noreferrer">
            <FontAwesomeIcon icon={faExternalLinkAlt} /> Voir
          </a>
        ) : (
          <span className="text-muted">-</span>
        ),
      id: "catalogUrl",
      cell: (info) => info.getValue(),
    },
    {
      header: "Crée le",
      footer: (props) => props.column.id,
      accessorFn: (row) =>
        DateTime.fromISO(row.createdAt).toFormat("dd-MM-yyyy"),
      id: "createdAt",
      cell: (info) => info.getValue(),
    },
  ]).current;

  return (
    <div className="formalys-page formalys-list-page">
      <AreYouSurToContinueModal
        ref={DeleteModalRef}
        onContinue={onDelete}
        title="Souhaitez-vous vraiment supprimé la catégorie qui a pour id : {{id}} ?"
      />
      <Table
        resource="organisations"
        HeaderComponent={AllOrganisationsHeader}
        columns={columns}
        state={state}
        tableName="organizations"
        onStateChange={setState}
        data={data?.data}
        meta={data?.meta}
      />
    </div>
  );
};

export default OrganisationsView;
